import React, { useEffect, useState } from 'react';
import { Button, Col, Row } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import { useLocation, useNavigate } from 'react-router-dom';

//Components
import TextField from '../../../components/TextField';
import CardIndicadores from './components/CardIndicadores';
import TabelaListagem from '../../../components/TabelaListagem/TabelaListagem';

//API
import tipoDeFiscalizacao_api from '../../../api/tipoDeFIscalizacao_api';
import gerenciamentoFiscalizacao_api from '../../../api/gerenciamentoFiscalizacao_api';

//Helpers
import { showMessage } from '../../../helpers/message';
import mascaras from '../../../helpers/mascaras';
import {
    COM_INFRACAO,
    initialStateConsultaGerenciamentoFiscalizacao,
    formatarValorIndicador,
    montarFiltroApi,
    montarIndicadoresComInfracao,
    montarIndicadoresSemInfracao,
    opcoesInfracao,
    opcoesTipoBusca,
    validarFiltroGerenciamentoFiscalizacao
} from './helpers/gerenciamentoFiscalizacao';

//Context
import { useAuth } from '../../../context/auth';

//Constants
import { actionsTable } from '../../../constants/MaterialTable';

function GerenciamentoDeFiscalizacao() {
    //State do filtro da consulta
    const [consulta, setConsulta] = useState(initialStateConsultaGerenciamentoFiscalizacao());

    //State com a lista dos tipos de fiscalização
    const [listTipoDeFiscalizacao, setListTipoDeFiscalizacao] = useState([]);

    //State dos indicadores exibidos nos cards
    const [indicadores, setIndicadores] = useState([]);

    //State da lista de resultados
    const [listaResultado, setListaResultado] = useState([]);

    //Controla se a consulta atual é Com ou Sem infração, para montar a tabela correta
    const [tipoConsultaExibida, setTipoConsultaExibida] = useState(COM_INFRACAO);

    const { setLoading } = useAuth();

    const navigate = useNavigate();

    //Filtro recebido via navegação (retorno da ficha da infração)
    const location = useLocation();
    const { filtro } = location.state ? location.state : {};

    const loadTipoDeFiscalizacao = () => {
        setLoading(true);
        tipoDeFiscalizacao_api.getList()
            .then(response => {
                response.data.map(value => {
                    value.value = value.id;
                    value.label = value.identificacao;
                    return value;
                })
                setListTipoDeFiscalizacao([{ value: "0", label: "Todos" }, ...response.data])
            })
            .catch(err => showMessage("Error", err?.message ?? "Error na operação.", "error", null))
            .finally(() => setLoading(false));
    };

    const loadGerenciamentoFiscalizacao = (filtroConsulta) => {
        setIndicadores([]);
        setListaResultado([]);
        setTipoConsultaExibida(filtroConsulta.infracao);

        const filtroApi = montarFiltroApi(filtroConsulta);
        const comInfracao = filtroConsulta.infracao === COM_INFRACAO;

        const consultarIndicadores = comInfracao
            ? gerenciamentoFiscalizacao_api.ConsultarComInfracao(filtroApi)
            : gerenciamentoFiscalizacao_api.ConsultarSemInfracao(filtroApi);

        const consultarLista = comInfracao
            ? gerenciamentoFiscalizacao_api.ListarInfracoes(filtroApi)
            : gerenciamentoFiscalizacao_api.ListarFiscalizacoesSemInfracao(filtroApi);

        setLoading(true);
        Promise.all([consultarIndicadores, consultarLista])
            .then(([respostaIndicadores, respostaLista]) => {
                setIndicadores(comInfracao
                    ? montarIndicadoresComInfracao(respostaIndicadores.data)
                    : montarIndicadoresSemInfracao(respostaIndicadores.data));

                setListaResultado((respostaLista.data ?? []).map(item => {
                    item.documentoInfrator = mascaras.cpfcnpjMask(item.documentoInfrator);
                    return item;
                }));
            })
            .catch(err => showMessage("Error", err?.message ?? "Error na operação.", "error", null))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadTipoDeFiscalizacao();

        if (filtro) {
            setConsulta({ ...filtro });
            loadGerenciamentoFiscalizacao(filtro);
        }
    }, []);

    const onChange = (e) => {
        const { name, value } = e.target;
        setConsulta({ ...consulta, [name]: value });
    };

    const onChangeSelect = (value, e) => {
        setConsulta({
            ...consulta,
            [e.name]: value ? value.value.toString() : null
        });
    };

    const handleConsultar = () => {
        const mensagem = validarFiltroGerenciamentoFiscalizacao(consulta);

        if (mensagem)
            return showMessage("Aviso", mensagem, "warning", null);

        loadGerenciamentoFiscalizacao(consulta);
    };

    const visualizarInfracao = (rowData) => {
        navigate("/admin/ficha-infracao", {
            state: {
                infracao_id: rowData.fiscalizacaoInfracao_Id,
                filtro: consulta,
                btnVoltar: "/admin/gerenciamentoDeFiscalizacao",
                originPath: "/admin/gerenciamentoDeFiscalizacao"
            }
        });
    };

    const visualizarFiscalizacao = (rowData) => {
        navigate("/admin/ficha-fiscalizacao", {
            state: {
                fiscalizacao_id: rowData.fiscalizacao_Id,
                filtro: consulta,
                btnVoltar: "/admin/gerenciamentoDeFiscalizacao",
                originPath: "/admin/gerenciamentoDeFiscalizacao"
            }
        });
    };

    //Cabeçalhos da lista de infrações (consultas Com Infração)
    const headersComInfracao = [
        { value: "Tipo de Fiscalização", objectValue: "tipoFiscalizacao", align: "left" },
        { value: "Infração", objectValue: "infracao", align: "left" },
        { value: "Número da Infração", objectValue: "numeroInfracao", align: "left" },
        { value: "Nome do Infrator", objectValue: "nomeInfrator", align: "left" },
        { value: "CPF/CNPJ do Infrator", objectValue: "documentoInfrator", align: "left" },
        { value: "Endereço da Infração", objectValue: "endereco", align: "left" },
        { value: "Data da Infração", objectValue: "dataInfracao", align: "left" },
        { value: "Data da Lavratura", objectValue: "dataLavratura", align: "left" },
        { value: "Usuário/Fiscal", objectValue: "usuario", align: "left" },
        { value: "Processo", objectValue: "numeroProcesso", align: "left" },
        { value: "Situação", objectValue: "situacao", align: "left" },
        { value: "Status do Boleto", objectValue: "statusBoleto", align: "left" },
        { value: "Status Dívida Ativa", objectValue: "statusDividaAtiva", align: "left" },
        { value: "Valor do Boleto", objectValue: "valorBoletoFormatado", align: "left" },
    ];

    //Cabeçalhos da lista de fiscalizações (consultas Sem Infração)
    const headersSemInfracao = [
        { value: "Tipo de Fiscalização", objectValue: "tipoFiscalizacao", align: "left" },
        { value: "Nome do Infrator", objectValue: "nomeInfrator", align: "left" },
        { value: "CPF/CNPJ do Infrator", objectValue: "documentoInfrator", align: "left" },
        { value: "Endereço", objectValue: "endereco", align: "left" },
        { value: "Data da Fiscalização", objectValue: "dataFiscalizacao", align: "left" },
        { value: "Data da Lavratura", objectValue: "dataLavratura", align: "left" },
        { value: "Usuário/Fiscal", objectValue: "usuario", align: "left" },
        { value: "Situação", objectValue: "situacao", align: "left" },
    ];

    const comInfracao = tipoConsultaExibida === COM_INFRACAO;

    //Formata o valor do boleto para exibição na tabela
    const itensTabela = listaResultado.map(item => ({
        ...item,
        valorBoletoFormatado: item.valorBoleto !== undefined
            ? formatarValorIndicador(item.valorBoleto)
            : ""
    }));

    return <>
        <h4 className='text-secondary m-0'>Gerenciamento de Fiscalização</h4>
        <Row className='mt-3'>
            <Col md={12} lg={3}>
                <TextField
                    type={"select"}
                    name={"tipoBusca"}
                    label={"* Tipo de Busca"}
                    searchable
                    options={opcoesTipoBusca}
                    onChange={onChangeSelect}
                    value={opcoesTipoBusca.filter(value => value.value == consulta.tipoBusca)}
                />
            </Col>
            <Col md={12} lg={3}>
                <TextField
                    type={"select"}
                    name={"infracao"}
                    label={"* Infração"}
                    searchable
                    options={opcoesInfracao}
                    onChange={onChangeSelect}
                    value={opcoesInfracao.filter(value => value.value == consulta.infracao)}
                />
            </Col>
            <Col md={12} lg={3}>
                <TextField
                    type={"date"}
                    name={"dataInicial"}
                    label={"Data Inicial"}
                    onChange={onChange}
                    value={consulta.dataInicial}
                />
            </Col>
            <Col md={12} lg={3}>
                <TextField
                    type={"date"}
                    name={"dataFinal"}
                    label={"Data Final"}
                    onChange={onChange}
                    value={consulta.dataFinal}
                />
            </Col>
            <Col md={12} lg={6}>
                <TextField
                    type={"select"}
                    name={"tipoFiscalizacao_Id"}
                    label={"Tipo de Fiscalização"}
                    searchable
                    options={listTipoDeFiscalizacao}
                    onChange={onChangeSelect}
                    value={listTipoDeFiscalizacao.filter(value => value.value == consulta.tipoFiscalizacao_Id)}
                />
            </Col>
            <Col md={12} lg={3}>
                <TextField
                    type={"text"}
                    name={"numeroInfracao"}
                    label={"Número da infração"}
                    onChange={onChange}
                    value={consulta.numeroInfracao}
                />
            </Col>
            <Col md={12} lg={3}>
                <TextField
                    type={"text"}
                    name={"documentoIdentificacao"}
                    label={"CPF/CNPJ do Infrator"}
                    mask={mascaras.cpfcnpjMask}
                    onChange={onChange}
                    value={consulta.documentoIdentificacao}
                />
            </Col>
            <Col md={12} lg={4}>
                <TextField
                    type={"text"}
                    name={"nome"}
                    label={"Nome/Razão Social do Infrator"}
                    onChange={onChange}
                    value={consulta.nome}
                />
            </Col>
            <Col md={12} lg={4}>
                <TextField
                    type={"text"}
                    name={"logradouro"}
                    label={"Logradouro"}
                    onChange={onChange}
                    value={consulta.logradouro}
                />
            </Col>
            <Col md={12} lg={2}>
                <TextField
                    type={"text"}
                    name={"numero"}
                    label={"Numero"}
                    mask={mascaras.numeroMask}
                    onChange={onChange}
                    value={consulta.numero}
                />
            </Col>
            <Col md={12} lg={2}>
                <TextField
                    type={"text"}
                    name={"bairro"}
                    label={"Bairro"}
                    onChange={onChange}
                    value={consulta.bairro}
                />
            </Col>
        </Row>
        <Row className='text-center align-items-center'>
            <Col md={12} lg={12}>
                <Button
                    color='primary'
                    className='text-white fw-bold'
                    onClick={handleConsultar}
                >
                    <FontAwesomeIcon icon={faFilter} /> Filtrar
                </Button>
            </Col>
        </Row>
        <Row className='mt-4'>
            {indicadores.length > 0 && <CardIndicadores indicadores={indicadores} />}
        </Row>
        <Row>
            {indicadores.length > 0 &&
                <TabelaListagem
                    actions={comInfracao
                        ? [actionsTable.visualizar(visualizarInfracao)]
                        : [actionsTable.visualizar(visualizarFiscalizacao)]}
                    title={comInfracao ? "Lista de infrações" : "Lista de fiscalizações"}
                    options={{ print: false, search: true, pagination: true }}
                    headers={comInfracao ? headersComInfracao : headersSemInfracao}
                    items={itensTabela}
                />
            }
        </Row>
    </>;
}

export default GerenciamentoDeFiscalizacao;
